import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import fetchAllParticipants from 'network/fetchAllParticipants';
import fetchParticipantsByCriteria from 'network/fetchParticipantsByCriteria';
import fetchExhibitorsByKeyword from 'network/fetchExhibitorsByKeyword';
import { APP_DOMAIN } from 'appenv';

import fetchSearchedProducts from 'network/fetchSearchedProducts';
import { participantEntity } from './participants';
import { selectWebinarsByKeyword, selectSpeakersByKeyword } from './webinars';

const DEFAULT_PARTICIPANTS = { pageInfo: { endCursor: null, hasNextPage: false }, edges: [] };
const DEFAULT_EHIBITORS = { pageInfo: { endCursor: null, hasNextPage: false }, edges: [] };

export const fetchSearchResults = createAsyncThunk(
  'searchResults/fetchAll',
  async ({ keyword, limit }, { getState }) => {
    const state = getState();
    const { participants, lastKeyword } = state.searchResults;
    const { features } = state.eventConfigs;
    const { availableLocales } = state.localization;
    const participantCursor = lastKeyword === keyword ? participants.lastCursor : undefined;
    const exhibitorCursor = keyword;

    try {
      const [
        {
          pageInfo: {
            endCursor,
            hasNextPage,
          },
          edges,
        },
        {
          pageInfo: {
            hasNextPage: productsHaveNextPage,
          } = { hasNextPage: false },
          hits: productResults = [],
        },
        {
          edges: boothEdges,
        },
      ] = await Promise.all([
        features.includes('hktdcFiltering') ? fetchParticipantsByCriteria(keyword, limit, participantCursor, {}, APP_DOMAIN).then((res) => res || DEFAULT_PARTICIPANTS).catch((_) => DEFAULT_PARTICIPANTS)
          : fetchAllParticipants(participantCursor, keyword, limit).then((res) => res || DEFAULT_PARTICIPANTS).catch((_) => DEFAULT_PARTICIPANTS),
        features.includes('productPage') ? fetchSearchedProducts(keyword) : {},
        fetchExhibitorsByKeyword(exhibitorCursor, keyword, limit, availableLocales).then((res) => res || DEFAULT_EHIBITORS).catch((_) => DEFAULT_EHIBITORS),
      ]);

      const participantResults = edges.map(({ cursor, node }) => {
        const id = node.uuid;
        return {
          ...node,
          id,
          cursor,
        };
      });

      const exhibitorResults = boothEdges?.map(({ cursor, node }) => {
        const { id } = node.searchable;
        return {
          ...node.searchable,
          id,
          cursor,
        };
      });

      const normalizedParticipantResults = normalize(participantResults, [participantEntity]);
      const webinarResults = selectWebinarsByKeyword(keyword, { limit })(getState());
      const speakerResults = selectSpeakersByKeyword(keyword, { limit })(getState());

      return {
        keyword,
        products: {
          entities: productResults.reduce((results, product) => {
            results[product.id] = product;
            return results;
          }, {}),
          hasNextPage: productsHaveNextPage,
        },
        participants: {
          entities: normalizedParticipantResults.entities.participants || {},
          lastCursor: (!lastKeyword.length || lastKeyword === keyword) ? endCursor : undefined,
          hasNextPage,
        },
        exhibitors: {
          entities: exhibitorResults.reduce((results, exhibitor) => {
            results[exhibitor.id] = exhibitor;
            return results;
          }, {}),
        },
        webinars: {
          entities: webinarResults.reduce((results, webinar) => {
            results[webinar.id] = webinar;
            return results;
          }, {}),
        },
        speakers: {
          entities: speakerResults.reduce((results, speaker) => {
            results[speaker.id] = speaker;
            return results;
          }, {}),
        },
      };
    } catch (error) {
      console.error(error);
    }
    return {
      keyword,
      participants: {},
      exhibitors: {},
      webinars: {},
      speakers: {},
    };
  },
  {
    condition: (_, { getState }) => {
      const { loading } = getState().searchResults;
      return !loading;
    },
  },
);

export const searchResultsSelector = createSelector(
  (state) => state.searchResults,
  ({
    participants, exhibitors, webinars, products, speakers,
  }) => ({
    participants: Object.values(participants.entities),
    exhibitors: Object.values(exhibitors.entities),
    webinars: Object.values(webinars.entities),
    products: Object.values(products.entities),
    speakers: Object.values(speakers.entities),
  }),
);

export const selectParticipantsFromSearchResults = (role) => (
  createSelector(
    [
      (state) => state.searchResults.participants,
      (state) => state.eventConfigs.features,
    ],
    (participants, features) => (
      (features.includes('hideAttendees') && role === 'attendee')
        ? Object.values(participants.entities).filter((p) => {
          const { customAttendeeFields } = p;
          // eslint-disable-next-line camelcase
          const vexpoRole = customAttendeeFields?.vexpo_role || 'attendee';
          return vexpoRole?.toLowerCase() === 'organizer' || vexpoRole?.toLowerCase() === 'exhibitor';
        })
        : Object.values(participants.entities)
    ),
  )
);

const initialState = {
  participants: {
    entities: {},
    lastCursor: undefined,
    hasNextPage: undefined,
  },
  exhibitors: {
    entities: {},
  },
  webinars: {
    entities: {},
  },
  products: {
    entities: {},
    hasNextPage: undefined,
    lastPage: undefined,
  },
  speakers: {
    entities: {},
  },
  lastKeyword: '',
  initialLoading: true,
  hasNextPage: true,
};

export const SearchResultsSlice = createSlice({
  name: 'searchResults',
  initialState,
  reducers: {
    resetSearchResults() {
      return initialState;
    },
  },
  extraReducers: {
    [fetchSearchResults.pending]: (state) => {
      state.loading = true;
    },
    [fetchSearchResults.rejected]: (state) => {
      state.loading = false;
    },
    [fetchSearchResults.fulfilled]: (state, action) => {
      const participants = action.payload.participants.entities || {};
      if (state.lastKeyword === action.payload.keyword) {
        state.participants.entities = {
          ...state.participants.entities,
          ...participants,
        };
      } else {
        state.participants.entities = participants;
      }
      state.participants.lastCursor = action.payload.participants.lastCursor;
      state.participants.hasNextPage = action.payload.participants.hasNextPage;

      const exhibitors = action.payload.exhibitors.entities || {};
      if (state.lastKeyword === action.payload.keyword) {
        state.exhibitors.entities = {
          ...state.exhibitors.entities,
          ...exhibitors,
        };
      } else {
        state.exhibitors.entities = exhibitors;
      }

      const webinars = action.payload.webinars.entities || {};
      if (state.lastKeyword === action.payload.keyword) {
        state.webinars.entities = {
          ...state.webinars.entities,
          ...webinars,
        };
      } else {
        state.webinars.entities = webinars;
      }

      const products = action.payload.products.entities || {};
      if (state.lastKeyword === action.payload.keyword) {
        state.products.entities = {
          ...state.products.entities,
          ...products,
        };
      } else {
        state.products.entities = products;
      }

      const speakers = action.payload.speakers.entities || {};
      if (state.lastKeyword === action.payload.keyword) {
        state.speakers.entities = {
          ...state.speakers.entities,
          ...speakers,
        };
      } else {
        state.speakers.entities = speakers;
      }

      state.lastKeyword = action.payload.keyword;
      state.initialLoading = false;
      state.loading = false;
      state.hasNextPage = state.participants.hasNextPage;
    },
  },
});

export default SearchResultsSlice.reducer;
